import React, { useState, useEffect } from 'react';
import { scriptApi } from '../services/api';
import './ExecutionHistory.css';

interface ExecutionEntry {
  execution_id: string;
  executed_at: string;
  success: boolean;
  execution_time: number;
  script_version?: number;
  input_file_ids: string[];
  input_file_names: string[];
  output_files: string[];
  output_file_details: Array<{ filename: string; file_id: string }>;
  error?: string;
  has_qa_validation: boolean;
  qa_summary?: {
    overall_status: string;
    summary: {
      total_validations: number;
      passed: number;
      failed: number;
      errors: number;
      pass_rate: number;
    };
  };
}

interface ExecutionHistoryResponse {
  script_id: string;
  current_version: number;
  total_executions: number;
  executions: ExecutionEntry[];
}

interface ExecutionHistoryProps {
  scriptId: string;
  onClose: () => void;
  onSelectExecution?: (executionId: string) => void;
}

const ExecutionHistory: React.FC<ExecutionHistoryProps> = ({ scriptId, onClose, onSelectExecution }) => {
  const [history, setHistory] = useState<ExecutionHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchHistory();
  }, [scriptId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/scripts/${scriptId}/execution-history`);
      if (!response.ok) {
        throw new Error('Failed to fetch execution history');
      }
      const data = await response.json();
      setHistory(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  const formatExecutionTime = (seconds: number) => {
    if (seconds < 1) {
      return `${(seconds * 1000).toFixed(0)}ms`;
    }
    return `${seconds.toFixed(2)}s`;
  };

  if (loading) {
    return (
      <div className="execution-history-modal">
        <div className="modal-backdrop" onClick={onClose} />
        <div className="modal-content">
          <div className="modal-header">
            <h3>Execution History</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="loading">Loading history...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="execution-history-modal">
        <div className="modal-backdrop" onClick={onClose} />
        <div className="modal-content">
          <div className="modal-header">
            <h3>Execution History</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="error-message">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="execution-history-modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content">
        <div className="modal-header">
          <h3>Execution History</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {history && (
            <>
              <div className="history-summary">
                <span>Current Version: v{history.current_version}</span>
                <span>•</span>
                <span>Total Executions: {history.total_executions}</span>
              </div>
              
              <div className="execution-list">
                {history.executions.map((execution) => (
                  <div
                    key={execution.execution_id}
                    className={`execution-item ${execution.success ? 'success' : 'failed'}`}
                    onClick={() => onSelectExecution?.(execution.execution_id)}
                  >
                    <div className="execution-header">
                      <div className="execution-status">
                        <span className="status-icon">
                          {execution.success ? '✓' : '✗'}
                        </span>
                        <span className="execution-time">
                          {formatDate(execution.executed_at)}
                        </span>
                      </div>
                      {execution.script_version && (
                        <span className="version-badge">v{execution.script_version}</span>
                      )}
                    </div>
                    
                    <div className="execution-details">
                      <div className="detail-row">
                        <span className="detail-label">Duration:</span>
                        <span className="detail-value">{formatExecutionTime(execution.execution_time)}</span>
                      </div>
                      
                      {execution.input_file_names.length > 0 && (
                        <div className="detail-row">
                          <span className="detail-label">Input Files:</span>
                          <span className="detail-value">
                            {execution.input_file_names.join(', ')}
                          </span>
                        </div>
                      )}
                      
                      {execution.output_file_details.length > 0 && (
                        <div className="detail-row">
                          <span className="detail-label">Output Files:</span>
                          <span className="detail-value">
                            {execution.output_file_details.map(f => f.filename).join(', ')}
                          </span>
                        </div>
                      )}
                      
                      {execution.has_qa_validation && execution.qa_summary && (
                        <div className="detail-row">
                          <span className="detail-label">QA Validation:</span>
                          <span className={`qa-status ${execution.qa_summary.overall_status}`}>
                            {execution.qa_summary.summary.passed}/{execution.qa_summary.summary.total_validations} passed
                          </span>
                        </div>
                      )}
                      
                      {execution.error && (
                        <div className="error-preview">
                          {execution.error.substring(0, 100)}...
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {history.executions.length === 0 && (
                  <div className="no-executions">
                    No execution history available for this script.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecutionHistory;